/*
  Drives the sticky add-to-cart bar.

  The bar never adds to the cart itself -- its button clicks the real one
  inside <product-form>. Everything that makes adding to cart correct (the
  selected variant, the quantity, sold-out handling, Dawn's AJAX cart and its
  error messages) then keeps working from one place, rather than living twice
  and drifting apart.

  The bar shows the button alone, so there is nothing else on it to keep in
  step -- only the label and the disabled state are mirrored.
*/
(function () {
  'use strict';

  const REAL_BUTTON = 'product-form button[name="add"]';
  // Set while the bar is up, so the chat button can be lifted clear of it.
  const ROOT_CLASS = 'sticky-atc-visible';

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.realButton = document.querySelector(REAL_BUTTON);

      // No buy button on the page at all -- a product with no variants for
      // sale. Nothing to mirror, so there is nothing worth showing.
      if (!this.realButton) {
        this.remove();
        return;
      }

      this.button = this.querySelector('[data-sticky-atc-add]');
      this.label = this.querySelector('[data-sticky-atc-label]');
      this.defaultLabel = this.label ? this.label.textContent.trim() : '';

      this.button.addEventListener('click', this.forwardClick.bind(this));

      this.watchVisibility();
      this.mirrorButton();
    }

    disconnectedCallback() {
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      this.buttonWatcher?.disconnect();
      document.documentElement.classList.remove(ROOT_CLASS);
    }

    forwardClick(event) {
      event.preventDefault();
      if (this.realButton.disabled) return;
      this.realButton.click();
    }

    /*
      Shown only once the real button has been scrolled PAST, not merely
      whenever it is off screen -- on a product page it starts below the fold,
      and reacting to that would put the bar up before the shopper has seen the
      button it duplicates.

      Measured on scroll rather than with an IntersectionObserver. An observer
      only reports threshold crossings, and a jump -- a back-to-top tap, an
      anchor, a restored scroll position -- can take the button from above the
      viewport to below it inside one frame without ever intersecting. No entry
      is emitted, and the bar stays up over a page that is back at the top.
      Reading the position on each scroll cannot miss that, and one
      getBoundingClientRect per animation frame costs nothing.
    */
    watchVisibility() {
      const measure = () => {
        this.ticking = false;
        this.toggle(this.realButton.getBoundingClientRect().bottom < 0);
      };

      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(measure);
      };

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      measure();
    }

    toggle(show) {
      this.classList.toggle('sticky-atc--visible', show);
      // Hidden from screen readers and off the tab order when it is off
      // screen: it is a duplicate control, not a second place to land.
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      this.button.tabIndex = show ? 0 : -1;
      document.documentElement.classList.toggle(ROOT_CLASS, show);
    }

    /*
      Dawn rewrites the real button's text and toggles `disabled` as the
      variant changes -- "Sold out", "Unavailable", back again. Copying both
      keeps the bar from offering something the page has already refused.
    */
    mirrorButton() {
      const sync = () => {
        const disabled = this.realButton.disabled;
        this.button.disabled = disabled;

        const realText = this.realButton.textContent.trim();
        if (this.label) this.label.textContent = realText || this.defaultLabel;
      };

      sync();
      this.buttonWatcher = new MutationObserver(sync);
      this.buttonWatcher.observe(this.realButton, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

  }

  if (!customElements.get('sticky-atc')) {
    customElements.define('sticky-atc', StickyAtc);
  }
})();
